import DesktopMonitorIcon from '@src/svgs/DesktopMonitorIcon';
import MobilePhoneIcon from '@src/svgs/MobilePhoneIcon';
import React, { useRef, useState } from 'react';
import styled from 'styled-components';

import { resolvePreviewSlug, SOCIAL_PREVIEWS } from './socialPreviewRegistry';

type Props = {
  content: string | null;
  authorName?: string;
  avatarUrl?: string;
  /**
   * Distribution channel slug of the prompt that produced this post
   * (PLAT-11038). Selects the preview to open; unknown, unset, and
   * preview-less channels fall back to LinkedIn.
   */
  channelSlug?: string | null;
};

const SocialPreview: React.FC<Props> = ({
  content,
  authorName,
  avatarUrl,
  channelSlug,
}) => {
  const [socialViewport, setSocialViewport] = useState<'desktop' | 'mobile'>(
    'desktop',
  );

  const targetSlug = resolvePreviewSlug(channelSlug);
  const [activeSlug, setActiveSlug] = useState(targetSlug);

  // Follow the incoming channel only when it actually changes (a new generation
  // or a different saved post). Re-renders that carry the same channel must not
  // clobber a tab the user picked by hand.
  const lastTargetSlugRef = useRef(targetSlug);
  if (lastTargetSlugRef.current !== targetSlug) {
    lastTargetSlugRef.current = targetSlug;
    setActiveSlug(targetSlug);
  }

  const active =
    SOCIAL_PREVIEWS.find(p => p.slug === activeSlug) ?? SOCIAL_PREVIEWS[0];
  const ActivePreview = active.Component;

  return (
    <SocialPreviewStack>
      <SocialControlsRow>
        <SocialPlatformTabs>
          {SOCIAL_PREVIEWS.map(preview => (
            <SocialPlatformTab
              key={preview.slug}
              active={preview.slug === active.slug}
              aria-pressed={preview.slug === active.slug}
              onClick={() => setActiveSlug(preview.slug)}
              aria-label={preview.label}
            >
              {preview.icon}
            </SocialPlatformTab>
          ))}
        </SocialPlatformTabs>
        <SocialViewportToggle>
          <SocialPlatformTab
            active={socialViewport === 'desktop'}
            onClick={() => setSocialViewport('desktop')}
            title="Desktop view"
          >
            <DesktopMonitorIcon />
            Desktop
          </SocialPlatformTab>
          <SocialPlatformTab
            active={socialViewport === 'mobile'}
            onClick={() => setSocialViewport('mobile')}
            title="Mobile view"
          >
            <MobilePhoneIcon />
            Mobile
          </SocialPlatformTab>
        </SocialViewportToggle>
      </SocialControlsRow>
      <ActivePreview
        content={content || ''}
        viewport={socialViewport}
        authorName={authorName}
        avatarUrl={avatarUrl}
      />
    </SocialPreviewStack>
  );
};

export default SocialPreview;

const SocialPreviewStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  align-items: stretch;
  width: 100%;
  max-width: 600px;
`;

const SocialControlsRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
`;

const SocialPlatformTabs = styled.div`
  display: flex;
  gap: 4px;
  padding: 4px;
  background: var(--popover);
  border-radius: 8px;
`;

const SocialViewportToggle = styled(SocialPlatformTabs)``;

const SocialPlatformTab = styled.button<{ active: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  border: none;
  background: ${props => (props.active ? 'var(--accent)' : 'transparent')};
  color: ${props =>
    props.active ? 'var(--background)' : 'var(--text-secondary)'};
  font-size: 12px;
  font-weight: 600;
  padding: 6px 12px;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s;

  svg {
    width: 16px;
    height: 16px;
  }

  &:hover {
    background: ${props =>
      props.active ? 'var(--accent)' : 'var(--sidebar-hover)'};
  }
`;
